import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

import type { BankFeedAutocompleteOption } from 'domain/bankFeed/schemas';
import { useBankFeedValueDates } from 'domain/bankFeed/queries';
import { useBankFeedRouteContext } from 'pages/company/BankFeedRoute/context';
import { computeEmptyVariant } from 'pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';
import SelectedAccountChip from 'pages/company/BankFeedRoute/components/FilterToolbar/shared/SelectedAccountChip';

const PAYMENT_DATE_WIDTH = 200;

// Credit Card tab payment-date filter (DA-19485). Single-value, searchable MUI Autocomplete whose
// options are the selected card + currency's discrete payment dates (value_date), fetched via
// useBankFeedValueDates. The chosen option's id (an ISO 'YYYY-MM-DD' string from the backend) is
// stored verbatim in `toolbarFilters.paymentDate` and sent back unchanged as date_from/date_to —
// no parsing/formatting. Replaces the shared preset DateRangeFilter on the CC tab only.
const CardDateRangeFilter: React.FC = () => {
  const intl = useIntl();
  const { toolbarFilters, setToolbarFilters, accountType, summary, isSummaryLoading } =
    useBankFeedRouteContext();
  const { paymentDate, currency, accountIds } = toolbarFilters;

  // CARD carries exactly one card id (DA-19483); the value-dates endpoint takes a single account_id.
  const accountId = accountIds[0] ?? null;

  const { data, isLoading: isOptionsLoading } = useBankFeedValueDates(accountType, accountId, currency);
  // options[0] is the newest payment date — the auto-select below relies on that order.
  const options = useMemo(() => data?.options ?? [], [data]);

  const [open, setOpen] = useState(false);

  const isEmptyView = computeEmptyVariant(summary, accountType) !== null;

  // The applied payment date is the option id itself — match it to a loaded option for the value.
  const selectedOption = useMemo(
    () => options.find((o) => o.id === paymentDate) ?? null,
    [options, paymentDate],
  );

  // Seed the newest payment date once the current (card, currency)'s options load, and re-seed when
  // that pair changes — the ticket's "select newest automatically on account/currency change". While
  // the new pair's options are still loading, clear the stale paymentDate so the grid gate
  // (BankFeedRoute) closes — otherwise the grid would fire one getRows with the new card/currency but
  // the previous pair's date. seededKeyRef marks a pair whose newest we've applied, so a manual pick
  // within the same pair is preserved; if that pick later falls out of a refreshed list, re-seed.
  const seededKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId == null || currency == null) return;
    const key = `${accountId}|${currency}`;
    if (seededKeyRef.current === key && selectedOption !== null) return;
    if (options.length === 0) {
      seededKeyRef.current = null;
      setToolbarFilters((prev) => (prev.paymentDate == null ? prev : { ...prev, paymentDate: null }));
      return;
    }
    seededKeyRef.current = key;
    setToolbarFilters((prev) =>
      prev.paymentDate === options[0].id ? prev : { ...prev, paymentDate: options[0].id },
    );
  }, [accountId, currency, options, selectedOption, setToolbarFilters]);

  const label = intl.formatMessage({
    id: 'company.bankFeed.transactions.toolbar.paymentDate.label',
    defaultMessage: 'Payment date',
  });

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, value: BankFeedAutocompleteOption) => {
      setToolbarFilters((prev) =>
        prev.paymentDate === value.id ? prev : { ...prev, paymentDate: value.id },
      );
    },
    [setToolbarFilters],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={label}
        slotProps={{
          input: {
            ...params.InputProps,
            endAdornment: (
              <>
                {isOptionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          },
        }}
      />
    ),
    [label, isOptionsLoading],
  );

  // Render the selected date as a chip (matching CardAccountFilter) when closed; null while open so
  // the search input shows for typing.
  const renderValue = useCallback(
    (value: BankFeedAutocompleteOption) => (open ? null : <SelectedAccountChip label={value.label} />),
    [open],
  );

  const isLoading = isSummaryLoading || isOptionsLoading;

  // A single Autocomplete under disableClearable needs a non-null value; until a payment date is
  // available (summary/options still loading, a pre-data empty state, or no options) render a
  // disabled placeholder field — mirrors CardAccountFilter.
  if (isEmptyView || options.length === 0 || selectedOption === null) {
    return (
      <TextField
        label={label}
        size="small"
        disabled
        sx={{ width: PAYMENT_DATE_WIDTH, backgroundColor: 'common.white' }}
        slotProps={{
          input: {
            endAdornment: isLoading ? <CircularProgress color="inherit" size={16} /> : undefined,
          },
        }}
      />
    );
  }

  return (
    <Autocomplete<BankFeedAutocompleteOption, false, true, false>
      disableClearable
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      size="small"
      sx={{
        width: PAYMENT_DATE_WIDTH,
        backgroundColor: 'common.white',
        '& .MuiInputBase-root': { flexWrap: 'nowrap' },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { pl: 1.75 },
        '& .MuiAutocomplete-input:first-of-type': open ? {} : { width: 0, minWidth: 0, p: 0 },
      }}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      renderValue={renderValue}
      renderInput={renderInput}
    />
  );
};

export default CardDateRangeFilter;
